import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
import { useSelector } from 'react-redux'
// components
import CircularProgress from '@mui/material/CircularProgress'
import { InputLabel } from 'components/FormElements'
// view components
import Form from './components/Form'
// graphql
import { GET_PARENT_FORM_FIELDS_QUERY } from 'graphql/risk/queries'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// constants
import { Forms } from 'constants/forms'
// types
import { IField, IParentFormPropertiesItem } from 'types/graphql'
// views
import { Group, GroupHeader, GroupsContainer, Postfix, RecordLink, Wrapper } from './views'
// utils
import { getParentFieldsGroups, IParentFieldMapping } from './utils'
import { getFieldsInitialValues } from 'utils/form'

interface Props {
  field: IField
}

const ParentFields: React.FC<Props> = ({ field }) => {
  const { client } = useApolloClient()

  const { risk } = React.useContext(RiskAPIContext)
  const { isRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const riskTemplates = useSelector(riskTemplatesSelector)

  const fieldMappings: IParentFieldMapping[] = (field.settings as any)?.fieldMappings ?? []

  const { data, loading } = useQuery<any>(GET_PARENT_FORM_FIELDS_QUERY, {
    variables: { riskId: risk?.id, fieldMappings },
    skip: isRiskTemplate || !risk?.id
  })

  const [initialValues, setInitialValues] = React.useState<Record<string, Record<string, any>>>({})

  const results: IParentFormPropertiesItem[] = data?.getParentFormProperties ?? []

  const groups = React.useMemo(
    () => getParentFieldsGroups({ results, riskTemplates, fieldMappings }),
    [data, riskTemplates, field.settings]
  )

  React.useEffect(() => {
    if (!groups.length) {
      return
    }

    const resolve = async () => {
      const entries = await Promise.all(
        groups.map(async ({ key, fields, fieldValues }) => {
          const values = await getFieldsInitialValues({
            client,
            fields,
            fieldValues,
            isRisk: true
          })

          return [key, values] as const
        })
      )

      setInitialValues(Object.fromEntries(entries))
    }

    resolve()
  }, [groups])

  if (!loading && !isRiskTemplate && !groups.length) {
    return null
  }

  return (
    <Wrapper isRiskTemplate={isRiskTemplate} data-testid='risk-field-value' data-entity-id={field.id}>
      <InputLabel label={field.label} required={field.required} />
      {!isRiskTemplate && loading && <CircularProgress size={16} />}
      <GroupsContainer>
        {groups.map(({ key, record, fields }) => (
          <Group key={key} data-testid='parent-related-form-group' data-entity-id={record.id}>
            <GroupHeader>
              <RecordLink href={`/forms/edit/${record.id}/template/${record.riskTemplate.id}`} target={'_blank'}>
                {record.riskTemplate?.name}
              </RecordLink>
              {record.customerUniqueIdPostfix && <Postfix> [{record.customerUniqueIdPostfix}]</Postfix>}
            </GroupHeader>
            <Form
              templateFields={fields}
              form={`${Forms.PARENT_FIELDS_FORM}_${field.id}_${key}`}
              initialValues={initialValues[key] ?? {}}
            />
          </Group>
        ))}
      </GroupsContainer>
    </Wrapper>
  )
}

export default ParentFields
